/* eslint-disable react/require-default-props */
import React, {useCallback} from 'react';
import {constant, noop} from 'lodash/fp';

import TabLayout from '/component/layout/TabLayout';
import PeopleReportDataRenderer from '/component/partial/report/PeopleReportDataRenderer';
import {useTranslation} from '/provider/TranslationProvider';

import {generateOnTabPress} from './PeopleReportWithTabsRenderer.util';
interface PeopleReportWithTabsRendererProps {
  reportName?: string;
  newReportName?: string;
  reportParams?: Record<string, any>;
  onRecordPress?: (record: any) => void;
  loadingComponent?: React.ComponentType<{total: number}> | null;
  loadingData?: {
    total?: number;
    newTotal?: number;
  };
  personRoleComponent?: React.ElementType;
  personInfoComponent?: React.ElementType;
  lastPaidRank?: string;
}

export const PeopleReportWithTabsRenderer: React.FC<
  PeopleReportWithTabsRendererProps
> = ({
  reportName = '',
  newReportName = '',
  reportParams = {},
  onRecordPress = noop,
  loadingComponent = null,
  loadingData = null,
  personRoleComponent = constant(null),
  personInfoComponent = constant(null),
  lastPaidRank = '',
}) => {
  const {ts} = useTranslation('subheader');
  const total = loadingData?.total ?? 0;
  const newTotal = loadingData?.newTotal ?? 0;

  const renderTabScreen = ({
    reportName: currentReportName,
    loadingData: currentLoadingData,
  }: {
    reportName: string;
    loadingData: {total: number};
  }) => (
    <PeopleReportDataRenderer
      reportName={currentReportName}
      loadingData={currentLoadingData}
      reportParams={reportParams}
      onRecordPress={onRecordPress}
      loadingComponent={loadingComponent}
      personRoleComponent={personRoleComponent}
      personInfoComponent={personInfoComponent}
    />
  );

  const tabs = [
    {
      name: 'all',
      label: ts('allTabLabel', `All (${total})`),
      component: useCallback(
        () =>
          renderTabScreen({
            loadingData: {total},
            reportName,
          }),
        [loadingData, reportName],
      ),
      onTabPress: () =>
        generateOnTabPress({
          tabName: 'all',
          buttonName: 'All',
          reportName,
          lastPaidRank,
        }),
    },
    {
      name: 'new',
      label: ts('newTabLabel', `New (${newTotal})`),
      component: useCallback(
        () =>
          renderTabScreen({
            loadingData: {total: newTotal},
            reportName: newReportName,
          }),
        [newReportName, newTotal],
      ),
      onTabPress: () =>
        generateOnTabPress({
          tabName: 'new',
          buttonName: 'New',
          reportName,
          lastPaidRank,
        }),
    },
  ];

  return <TabLayout tabs={tabs} />;
};
